import {AppFirebaseState} from "../types/AppFirebaseState";
import {eventDayKey as sofiaDay} from "../calendar/eventPeriod";
export {eventDayKey as sofiaDay} from "../calendar/eventPeriod";

export type WarehouseDepartment = "montage" | "catering" | "store";
export interface WarehouseDayLock {locked: boolean; unlockUntil: number}
export interface WarehouseJobState {
    enabled: boolean;
    initialized: boolean;
    startDay: string;
    lastCompletedRunDay: string;
    completedAt: number;
    status: string;
    error: string;
    leaseUntil?: number;
}
export interface WarehouseLocksState extends AppFirebaseState<string[]> {
    scope?: string;
    department?: string;
    firstDay?: string;
    lastDay?: string;
    records?: Record<string, WarehouseDayLock>;
    job?: WarehouseJobState;
    confirmedCycle?: number;
    now?: number;
}
export interface WarehouseAccess {
    blocked: boolean;
    locked: boolean;
    pending: boolean;
    message: string;
}

export function addCalendarDays(day: string, count: number): string {
    const date = new Date(`${day}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + count);
    return date.toISOString().slice(0, 10);
}

export function warehouseDateId(date: Date): string {
    const [year, month, day] = sofiaDay(date).split("-");
    return `${day}.${month}.${year}`;
}

export function warehouseDayKey(id: string): string {
    const [day, month, year] = id.split(".");
    return `${year}-${month}-${day}`;
}

/** Reads subscribed flags, not Firestore. The age check only covers the scheduler's pending window. */
export function warehouseAccess(state: WarehouseLocksState, dateId: string, department: string): WarehouseAccess {
    const open: WarehouseAccess = {blocked: false, locked: false, pending: false, message: ""};
    if (!["montage", "catering", "store"].includes(department)) return open;
    const day = warehouseDayKey(dateId);
    const unavailable = !state.scope || state.department !== department ||
        !state.firstDay || !state.lastDay || day < state.firstDay || day >= state.lastDay;
    if (unavailable || state.loading || state.error) {
        return {...open, blocked: true, pending: !state.error,
            message: state.error || "Зареждане на заключването…"};
    }
    const record = state.records?.[dateId];
    const now = Math.max(state.now || 0, Date.now());
    if (state.job?.initialized && state.job.startDay && day < state.job.startDay &&
        !(record && !record.locked && record.unlockUntil > now)) {
        return {...open, blocked: true, locked: true, message: "Датата е преди началото на складовата история."};
    }
    if (record?.locked || state.data.includes(dateId)) {
        return {...open, blocked: true, locked: true, message: "Складът е заключен за тази дата."};
    }
    const today = sofiaDay(new Date(now));
    const job = state.job;
    if (job?.enabled && job.initialized && day <= addCalendarDays(today, -3)) {
        if (record && record.unlockUntil > now) return open;
        if (job.lastCompletedRunDay !== today || state.confirmedCycle !== job.completedAt ||
            (record && record.unlockUntil > 0 && record.unlockUntil <= now)) {
            const clock = new Intl.DateTimeFormat("en-GB", {timeZone: "Europe/Sofia",
                hour: "2-digit", minute: "2-digit", hourCycle: "h23"}).format(new Date(now));
            const [hour, minute] = clock.split(":").map(Number);
            const failed = job.status === "error" ||
                (job.status === "running" && !!job.leaseUntil && job.leaseUntil <= now) ||
                (job.lastCompletedRunDay !== today && hour * 60 + minute >= 10);
            return {...open, blocked: true, pending: !failed,
                message: failed ? "Заключването не е обновено. Необходима е проверка от администратор." :
                    "Обновяване на заключването…"};
        }
    }
    return open;
}
